import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api, { getImageUrl } from '../api';
import { useAuth } from '../context/AuthContext';
import { productSlug } from '../utils/slug';

const PERKS = [
  { icon: '🏷️', title: '10% Off Every Order', desc: 'Save on every single purchase — automatically applied at checkout' },
  { icon: '🚚', title: 'Free Shipping Always', desc: 'Free standard shipping on every order, no minimum required' },
  { icon: '⚡', title: 'Early Access', desc: 'Shop new arrivals 24 hours before they go public' },
  { icon: '🎂', title: 'Birthday Discount', desc: 'Extra 15% off during your birthday month' },
  { icon: '💬', title: 'Priority Support', desc: 'Skip the queue — your emails are answered first' },
  { icon: '👑', title: 'Members-Only Products', desc: 'Access exclusive styles not available to regular shoppers' },
];

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function Membership() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vipProducts, setVipProducts] = useState([]);
  const [subscribing, setSubscribing] = useState(false);
  const [billing, setBilling] = useState('annual');
  const [birthdayMonth, setBirthdayMonth] = useState('');
  const [savingBirthday, setSavingBirthday] = useState(false);

  useEffect(() => {
    // Fetch VIP-only products — preview for all, full for VIP
    api.get('/products/vip-preview').then(r => setVipProducts(r.data || [])).catch(() => {});
    if (user) {
      api.get('/membership').then(r => setMembership(r.data)).catch(() => {}).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [user]);

  // Check for success/cancel redirect from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (params.get('success') && sessionId && user) {
      // Verify session directly — works even without webhook (e.g. localhost)
      api.post('/membership/verify-session', { session_id: sessionId })
        .then(r => { setMembership(r.data); })
        .catch(() => {
          // Fallback: just re-fetch membership status
          api.get('/membership').then(r => setMembership(r.data)).catch(() => {});
        });
    } else if (params.get('success')) {
      setTimeout(() => {
        api.get('/membership').then(r => setMembership(r.data)).catch(() => {});
      }, 3000);
    }
  }, []);

  const handleSubscribe = async () => {
    if (!user) { navigate('/login?redirect=/membership'); return; }
    setSubscribing(true);
    try {
      const r = await api.post('/membership/subscribe', { billing });
      window.location.href = r.data.url;
    } catch (err) {
      alert(err.response?.data?.error || 'Something went wrong');
    } finally {
      setSubscribing(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel your VIP membership? You keep access until the end of your billing period.')) return;
    await api.post('/membership/cancel');
    setMembership(m => ({ ...m, status: 'cancelled' }));
  };

  const handleSaveBirthday = async () => {
    if (!birthdayMonth) return;
    setSavingBirthday(true);
    await api.post('/membership/birthday', { month: parseInt(birthdayMonth) }).catch(() => {});
    setMembership(m => ({ ...m, birthday_month: parseInt(birthdayMonth) }));
    setSavingBirthday(false);
  };

  const isActive = membership?.status === 'active';
  const params = new URLSearchParams(window.location.search);
  const justJoined = params.get('success') === '1';

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
      {/* Hero */}
      <div style={{ background: 'linear-gradient(135deg,#1e1b4b 0%,#4c1d95 50%,#4f46e5 100%)', color: 'white', padding: '60px 24px', textAlign: 'center' }}>
        <div style={{ fontSize: 56, marginBottom: 12 }}>💎</div>
        <h1 style={{ fontSize: 'clamp(28px,5vw,46px)', fontWeight: 900, margin: '0 0 16px', letterSpacing: -1 }}>StyleMe VIP</h1>
        <p style={{ fontSize: 18, opacity: 0.9, maxWidth: 520, margin: '0 auto', lineHeight: 1.6 }}>
          Shop smarter. Save more. Get the exclusive perks you deserve.
        </p>
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '48px 24px' }}>

        {/* Welcome new user banner */}
        {params.get('welcome') === '1' && !isActive && (
          <div style={{ background: 'linear-gradient(135deg,#fef9e7,#fef0c0)', border: '1px solid #4f46e5', borderRadius: 12, padding: '20px 24px', marginBottom: 32, textAlign: 'center' }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>🎉</div>
            <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 6 }}>Welcome to StyleMe!</div>
            <p style={{ color: '#374151', margin: 0, fontSize: 15 }}>Your account is ready. Upgrade to <strong>VIP</strong> to unlock 10% off every order + free shipping from day one.</p>
          </div>
        )}

        {/* Success banner */}
        {justJoined && (
          <div style={{ background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 12, padding: '16px 20px', marginBottom: 32, textAlign: 'center', fontSize: 16, fontWeight: 700, color: '#065f46' }}>
            🎉 Welcome to StyleMe VIP! Your membership is now active.
          </div>
        )}

        {/* Active membership card */}
        {isActive && (
          <div style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', borderRadius: 16, padding: '28px 32px', color: 'white', marginBottom: 40, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <div style={{ fontSize: 14, opacity: 0.8, marginBottom: 4 }}>Your membership</div>
              <div style={{ fontSize: 28, fontWeight: 900 }}>💎 StyleMe VIP</div>
              <div style={{ opacity: 0.85, marginTop: 6, fontSize: 14 }}>
                {membership.billing_period === 'annual' ? '$79/year' : '$9.99/month'} ·{' '}
                {membership.current_period_end ? `Renews ${new Date(membership.current_period_end).toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'})}` : 'Active'}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-end' }}>
              {/* Birthday picker — locked once set */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {membership.birthday_month ? (
                  <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 8, padding: '6px 14px', fontSize: 13, color: 'white' }}>
                    🎂 Birthday: {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][membership.birthday_month - 1]} (locked)
                  </div>
                ) : (
                  <>
                    <select value={birthdayMonth} onChange={e => setBirthdayMonth(e.target.value)}
                      style={{ padding: '6px 10px', borderRadius: 8, border: 'none', fontSize: 13, background: 'rgba(255,255,255,0.2)', color: 'white', cursor: 'pointer' }}>
                      <option value="">🎂 Birthday month</option>
                      {MONTHS.map((m,i) => <option key={i} value={i+1} style={{ color: '#111' }}>{m}</option>)}
                    </select>
                    <button onClick={handleSaveBirthday} disabled={savingBirthday || !birthdayMonth}
                      style={{ padding: '6px 14px', background: 'white', color: '#4f46e5', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                      {savingBirthday ? '...' : 'Save'}
                    </button>
                  </>
                )}
              </div>
              <button onClick={handleCancel} style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '6px 14px', fontSize: 12, cursor: 'pointer' }}>
                Cancel membership
              </button>
              <a href="https://billing.stripe.com/p/login/aFafZh4Ni7fv5EL9Kp8so00" target="_blank" rel="noreferrer"
                style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '6px 14px', fontSize: 12, cursor: 'pointer', textDecoration: 'none', textAlign: 'center' }}>
                💳 Manage Billing
              </a>
            </div>
          </div>
        )}

        {/* Perks grid */}
        <h2 style={{ fontSize: 24, fontWeight: 900, marginBottom: 24, textAlign: 'center' }}>Everything included with VIP</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 16, marginBottom: 48 }}>
          {PERKS.map((p, i) => (
            <div key={i} style={{ background: 'white', borderRadius: 12, padding: '20px 22px', border: '1px solid #e5e7eb', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
              <div style={{ fontSize: 28, flexShrink: 0 }}>{p.icon}</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{p.title}</div>
                <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>{p.desc}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Pricing — only show if not active */}
        {!isActive && (
          <div style={{ background: 'white', borderRadius: 20, border: '2px solid #4f46e5', padding: '40px', maxWidth: 480, margin: '0 auto', textAlign: 'center', boxShadow: '0 8px 32px rgba(201,162,39,0.12)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#4f46e5', letterSpacing: 2, marginBottom: 8 }}>STYLEME VIP</div>
            {/* Billing toggle */}
            <div style={{ display: 'inline-flex', background: '#f3f4f6', borderRadius: 100, padding: 4, marginBottom: 28, gap: 4 }}>
              {['monthly','annual'].map(b => (
                <button key={b} onClick={() => setBilling(b)}
                  style={{ padding: '8px 22px', borderRadius: 100, border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                    background: billing === b ? '#4f46e5' : 'transparent',
                    color: billing === b ? 'white' : '#6b7280' }}>
                  {b === 'monthly' ? 'Monthly' : 'Annual'}
                  {b === 'annual' && <span style={{ marginLeft: 6, fontSize: 11, background: '#10b981', color: 'white', borderRadius: 4, padding: '1px 6px' }}>Save 34%</span>}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 52, fontWeight: 900, color: '#111', lineHeight: 1 }}>
              {billing === 'annual' ? '$79' : '$9.99'}
            </div>
            <div style={{ color: '#6b7280', marginBottom: 8, fontSize: 15 }}>
              {billing === 'annual' ? 'per year ($6.58/mo)' : 'per month'}
            </div>
            {billing === 'annual' && (
              <div style={{ fontSize: 13, color: '#10b981', fontWeight: 600, marginBottom: 20 }}>
                You save $40.88 vs monthly billing
              </div>
            )}
            <button onClick={handleSubscribe} disabled={subscribing}
              style={{ width: '100%', padding: '16px', background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', color: 'white', border: 'none', borderRadius: 12, fontWeight: 800, fontSize: 16, cursor: 'pointer', marginTop: 16 }}>
              {subscribing ? 'Redirecting...' : '💎 Become VIP'}
            </button>
            <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 12 }}>Cancel anytime · Secure payment via Stripe</p>
          </div>
        )}

        {/* Savings calculator */}
        {!isActive && (
          <div style={{ textAlign: 'center', marginTop: 40, padding: 24, background: '#fef9e7', borderRadius: 16 }}>
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 8 }}>💡 Does VIP pay for itself?</div>
            <p style={{ color: '#374151', fontSize: 14, margin: 0 }}>
              Spend $100/month → save $10 on discounts + $7.99 on shipping = <strong>$17.99 saved</strong>.<br />
              VIP pays for itself in just <strong>one order</strong>.
            </p>
          </div>
        )}

        {/* VIP Exclusive Products */}
        {vipProducts.length > 0 && (
          <div style={{ marginTop: 56 }}>
            <h2 style={{ fontSize: 24, fontWeight: 900, marginBottom: 8 }}>💎 Members-Only Exclusives</h2>
            <p style={{ color: '#6b7280', fontSize: 15, marginBottom: 24 }}>
              {isActive ? 'Your exclusive products — only available to VIP members.' : 'Join VIP to unlock these exclusive products.'}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 16 }}>
              {vipProducts.map(p => {
                const thumb = p.cover || p.images?.[0]?.image_url || p.image_url;
                const imgUrl = thumb ? (thumb.startsWith('http') ? thumb : `${process.env.REACT_APP_API_URL || ''}${thumb}`) : null;
                return (
                  <div key={p.id} style={{ position: 'relative', borderRadius: 14, overflow: 'hidden', background: 'white', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                    {imgUrl ? (
                      <img src={imgUrl} alt={p.name} style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block', filter: isActive ? 'none' : 'blur(8px)' }} />
                    ) : (
                      <div style={{ height: 200, background: 'linear-gradient(135deg,#ede9fe,#ddd6fe)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40 }}>💎</div>
                    )}
                    {!isActive && (
                      <div style={{ position: 'absolute', inset: 0, background: 'rgba(79,70,229,0.15)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ fontSize: 28, marginBottom: 4 }}>🔒</div>
                        <div style={{ color: 'white', fontWeight: 800, fontSize: 13, textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>VIP Only</div>
                      </div>
                    )}
                    <div style={{ padding: '10px 12px 14px' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, color: '#374151', lineHeight: 1.3 }}>{p.name}</div>
                      {isActive ? (
                        <Link to={`/product/${productSlug(p.name, p.id)}`} style={{ fontSize: 13, fontWeight: 700, color: '#4f46e5', textDecoration: 'none' }}>
                          ${p.price?.toFixed(2)} — Shop →
                        </Link>
                      ) : (
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#9ca3af' }}>Members only</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {!isActive && (
              <div style={{ textAlign: 'center', marginTop: 24 }}>
                <p style={{ color: '#6b7280', marginBottom: 12 }}>Join VIP to shop these exclusive products</p>
                <button onClick={() => document.querySelector('.membership-pricing')?.scrollIntoView({ behavior: 'smooth' })}
                  style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', color: 'white', border: 'none', borderRadius: 100, padding: '12px 32px', fontWeight: 700, cursor: 'pointer', fontSize: 15 }}>
                  Join VIP to Unlock →
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
